import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { graphql } from './client';
import { DEFAULT_CUTOVER_DATE } from '@referee/shared-client/season';

export const GET_ORGANIZATION = graphql(/* GraphQL */ `
  query GetOrganization($id: String!) {
    getOrganization(params: { id: $id }) {
      id
      name
      logo
      primaryColor
      welcomeText
      cutoverDate
    }
  }
`);

interface OrganizationContextValue {
  organizationId: string;
  name: string;
  logo: string;
  primaryColor: string;
  welcomeText: string | null;
  cutoverDate: string;
  loading: boolean;
  error: boolean;
}

const OrganizationCtx = createContext<OrganizationContextValue | null>(null);

export function useOrganization(): OrganizationContextValue {
  const ctx = useContext(OrganizationCtx);
  if (!ctx) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return ctx;
}

function getOrganizationId(): string {
  const hostname = window.location.hostname;

  // Dev: check URL params or localStorage
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search);
    const devOrg = params.get('dev-org') ?? localStorage.getItem('referee:dev:org');
    if (devOrg) {
      localStorage.setItem('referee:dev:org', devOrg);
      return devOrg;
    }
    return 'dev';
  }

  // Production: extract subdomain
  return hostname.split('.')[0];
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const organizationId = useMemo(() => getOrganizationId(), []);

  const { data, loading, error } = useQuery(GET_ORGANIZATION, {
    variables: { id: organizationId },
  });

  const org = data?.getOrganization;
  const name = org?.name ?? organizationId;
  const logo = org?.logo ? `/logo/${org.logo}` : '';
  const primaryColor = org?.primaryColor ?? '';
  const welcomeText = org?.welcomeText ?? null;
  const cutoverDate = org?.cutoverDate ?? DEFAULT_CUTOVER_DATE;
  const hasError = Boolean(error);

  const value = useMemo(() => ({
    organizationId,
    name,
    logo,
    primaryColor,
    welcomeText,
    cutoverDate,
    loading,
    error: hasError,
  }), [organizationId, name, logo, primaryColor, welcomeText, cutoverDate, loading, hasError]);

  return <OrganizationCtx value={value}>{children}</OrganizationCtx>;
}
